import { VideoRecordType } from '@distribute/shared/api-types/video-record';

import { FC, useMemo } from 'react';

import { generatePath } from 'react-router-dom';

import {
  filterByRecordsMy,
  filterByRecordsShared,
} from '../../../entities/recordings';
import { VideoCardTablePreview } from '../../../entities/video';

import { authUserModel } from '../../../entities/auth-user';

import { recordingsModel } from '../../../features/recordings';

import { useDispatch, useSelector } from 'react-redux';
import { SearchModalEmpty } from './SearchModalEmpty';
import { contains } from '../../../utils/string';

import {
  checkIsRecordConverting,
  checkIsRecordConvertingFailed,
} from '../../../features/recordings/lib';

import { getMuxImage } from '@distribute/shared/utils';

import { SearchModalNotFound } from './SearchModalNotFound';
import { SearchModalLabel } from './SearchModalLabel';
import { SearchModalContentItem } from './SearchModalContentItem';

import { navigate } from '../../../processes/navigation';
import { QUICK_RECORDING_ROUTE } from '../../../entities/history';

type Props = {
  search: string;
  onClear: () => void;
};

export const SearchModalVideoRecords: FC<Props> = ({ search, onClear }) => {
  const records = useSelector(recordingsModel.selectors.selectRecords);
  const currentUser = useSelector(authUserModel.selectors.selectUserWithError);

  const filteredRecords = useMemo(
    () =>
      records.filter((record) => contains(record.name || 'Untitled', search)),
    [records, search]
  );

  const { personal, team } = useMemo(() => {
    return {
      personal: filterByRecordsMy(filteredRecords, currentUser.id),
      team: filterByRecordsShared(filteredRecords, currentUser.id),
    };
  }, [filteredRecords, currentUser.id]);

  if (search.length === 0) {
    return <SearchModalEmpty />;
  }

  if (filteredRecords.length === 0) {
    return <SearchModalNotFound onClear={onClear} name="recordings" />;
  }

  return <Content personal={personal} team={team} />;
};

const Content = ({
  personal,
  team,
}: {
  personal: VideoRecordType[];
  team: VideoRecordType[];
}) => {
  return (
    <div className="flex flex-col gap-y-4">
      {personal.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <SearchModalLabel>My recordings</SearchModalLabel>
          {personal.map((record) => (
            <ContentItem key={record.id} record={record} />
          ))}
        </div>
      )}
      {team.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <SearchModalLabel>Team recordings</SearchModalLabel>
          {team.map((record) => (
            <ContentItem key={record.id} record={record} />
          ))}
        </div>
      )}
    </div>
  );
};

const ContentItem = ({ record }: { record: VideoRecordType }) => {
  const dispatch = useDispatch();

  return (
    <SearchModalContentItem
      onClick={() => {
        dispatch(
          navigate({
            to: generatePath(QUICK_RECORDING_ROUTE, { prefix: record.prefix }),
          })
        );
      }}
    >
      <VideoCardTablePreview
        isFailed={checkIsRecordConvertingFailed({
          status: record.status,
          statusConvert: record.statusConvert,
        })}
        isLoading={checkIsRecordConverting(record.statusConvert)}
        url={getMuxImage(record.playbackId || '')}
        name={record.name}
      />
    </SearchModalContentItem>
  );
};
